import { Injectable, inject } from '@angular/core';
import {combineLatest, from, Observable, of} from 'rxjs';
import {findUniqueMatchWithId, readDocumentWithId} from '../common/firestore-utils';
import {TenantService} from './tenant.service';
import {first, map, switchMap} from 'rxjs/operators';
import {Course} from '../models/course.model';
import {Update} from '@ngrx/entity';
import {CourseSection} from '../models/course-section.model';
import {
  collection,
  deleteDoc,
  doc,
  documentId,
  Firestore,
  limit,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch
} from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';
import {QueryConstraint} from '@firebase/firestore';


@Injectable()
export class CoursesDBService {
  private tenant = inject(TenantService);
  private firestore = inject(Firestore);
  private fs = inject(FirestoreService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  findCourseByUrl(courseUrl: string) {

    const constraints: QueryConstraint[] = [
      where('url', '==', courseUrl)
    ]

    const courseQuery = query(
      collection(this.firestore, this.coursesPath),
      ...constraints
    )

    const courseSnap = this.fs.collectionData<Course>(courseQuery, { idField: 'id' });

    return findUniqueMatchWithId(courseSnap).pipe(first()) as Observable<Course>;

  }

  findCourseBySeqNo(seqNo: number) {

    const constraints: QueryConstraint[] = [
      where('seqNo', '==', seqNo)
    ]

    const courseQuery = query(
      collection(this.firestore, this.coursesPath),
      ...constraints
    )

    const courseSnap = this.fs.collectionData(courseQuery, { idField: 'id' });

    return findUniqueMatchWithId(courseSnap).pipe(first()) as Observable<Course>;

  }

  suscribeToCourse(courseId: string): Observable<Course> {
    const courseDocRef = doc(this.firestore,this.coursesPath + '/' + courseId)
    const cousreDocData = this.fs.docData(courseDocRef, {
      idField: 'id'
    })

    return readDocumentWithId(cousreDocData)

    // return readDocumentWithId(this.afs.doc(this.coursesPath + '/' + courseId));
  }

  findAllCourses(includeDraftCourses: boolean = true): Observable<Course[]> {

    const constraints: QueryConstraint[] = [
      orderBy('seqNo')
    ];

    if (!includeDraftCourses) {
      constraints.push(where('status', '==', 'published'))
    }

    const q = query(
      collection(this.firestore, this.coursesPath),
      ...constraints
    )

    const courseSnap = this.fs.collectionData<Course>(q, { idField: 'id' });

    return courseSnap.pipe(first()) as Observable<Course[]>;
  }

  hasUploadedVideoLessons(): Observable<boolean> {
    return this.findAllCourses().pipe(
      first(),
      switchMap(courses => {
        if (courses.length === 0) return of(false);
        const checks = courses.map(course => {
          const q = query(
            collection(this.firestore, this.tenant.path(`courses/${course.id}/lessons`)),
            where('videoType', '==', 'och'),
            limit(1)
          );
          return from(this.fs.getDocs(q)).pipe(map(snap => !snap.empty));
        });
        return combineLatest(checks).pipe(map(results => results.some(Boolean)));
      })
    );
  }

  findAllCoursesByIds(courseIds: string[], includeDraftCourses: boolean = true): Observable<Course[]> {

    const constraints: QueryConstraint[] = [
      orderBy('seqNo'),
      where(documentId(), 'in', courseIds)
    ];

    if (!includeDraftCourses) {
      constraints.push(where('status', '==', 'published'))
    }

    const q = query(
      collection(this.firestore, this.coursesPath),
      ...constraints
    )

    const courseSnap = this.fs.collectionData(q, {idField: 'id'});
    const courses = courseSnap.pipe(first()) as Observable<Course[]>;
    return courses;
  }

  createNewCourse(course: Course): Observable<null> {
    const docRef = doc(this.firestore,`${this.coursesPath}/${course.id}`)
    return from(setDoc(docRef, course)) as Observable<null>;
  }

  findNextCourseSeqNo() {
    return this.findAllCourses()
      .pipe(
        map(courses => {

          if (courses?.length === 0) {
            return 1;
          }

          const allSeqNos = courses?.map((course) => course.seqNo);

          const maxSeqNo = Math.max(...allSeqNos) || 0;

          return (maxSeqNo + 1);
        })
      )
  }

  deleteCourseDraft(courseId: string): Observable<any> {
    return from(deleteDoc(doc(this.firestore,`${this.coursesPath}/${courseId}`)))
  }

  /**
   * Every edit to a course DOCUMENT funnels through here (the updateCourse effect, plus
   * publish/unpublish), so this is where the course's last-edited time is stamped.
   *
   * Nothing used to write `updatedAt` on a course - only lessons, reviews, certificates etc.
   * wrote their own - so every course document had no timestamp at all. The courses-page
   * "Last updated" sort therefore compared 0 against 0 for every product: a stable no-op that
   * rendered the exact same order as "Manual order", which is why editing a course title or
   * description never moved it to the top.
   *
   * Note: reordering goes through updateCourses() (batch write) and is deliberately NOT
   * stamped - dragging a card to a new position is not an edit of the product.
   */
  saveCourse(courseId: any, props: Partial<Course>): Observable<any> {
    const docRef = doc(this.firestore,`${this.coursesPath}/${courseId}`)
    return from(updateDoc(docRef, { ...props, updatedAt: Timestamp.now() }))
  }

  private get coursesPath() {
    return this.tenant.path('courses');
  }

  updateCourses(changes: Update<Course>[]) {

    const batch = writeBatch(this.firestore)

    changes.forEach(change => {
      const courseRef = doc(this.firestore,`schools/${this.tenant.id}/courses/${change.id}`);
      batch.update(courseRef, change.changes);
    });

    return from(batch.commit());

  }

  updateCourseSections(courseId: string, changes: Update<CourseSection>[]) {

    const batch = writeBatch(this.firestore)

    changes.forEach(change => {
      const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}/sections/${change.id}`);
      batch.update(courseRef, change.changes);
    });

    return from(batch.commit());

  }

  findCourseByTenantAndId(tenantId: string, courseId: string) {
    const courseRef = doc(this.firestore,`schools/${tenantId}/courses/${courseId}`)

    return readDocumentWithId<Course>(this.fs.docData(courseRef, { idField: 'id' })).pipe(first());
  }
}




